import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import ConfirmDialog from './ConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPayoutCenter = () => {
  const [duePayouts, setDuePayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [confirmState, setConfirmState] = useState(null);

  useEffect(() => {
    loadDuePayouts();
  }, []);

  const loadDuePayouts = async () => {
    try {
      const response = await axios.get(`${API}/admin/payouts/due`);
      setDuePayouts(response.data);
      setSelectedIds([]);
    } catch (error) {
      toast.error('Fehler beim Laden der fälligen Auszahlungen');
    } finally {
      setLoading(false);
    }
  };

  const confirmPayout = async (payoutId, reference, note, receiptFile) => {
    try {
      await axios.post(`${API}/admin/payouts/${payoutId}/confirm`, {
        reference: reference || '',
        note: note || '',
      });
      
      if (receiptFile) {
        const formData = new FormData();
        formData.append('file', receiptFile);
        await axios.post(`${API}/admin/payouts/${payoutId}/upload-receipt`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      
      toast.success('Auszahlung bestätigt' + (receiptFile ? ' und Beleg hochgeladen' : ''));
      loadDuePayouts();
    } catch (error) {
      toast.error('Fehler beim Bestätigen: ' + (error.response?.data?.detail || error.message));
    }
  };

  const bulkConfirm = () => {
    if (selectedIds.length === 0) {
      toast.error('Bitte wählen Sie mindestens eine Auszahlung');
      return;
    }
    const total = duePayouts
      .filter((p) => selectedIds.includes(p.id))
      .reduce((s, p) => s + p.amount_eur, 0);
    setConfirmState({
      title: `${selectedIds.length} Auszahlung${selectedIds.length > 1 ? 'en' : ''} bestätigen?`,
      message: `Gesamtbetrag: ${new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(total)}\n\nDie Kunden werden per E-Mail benachrichtigt.`,
      confirmLabel: 'Bestätigen',
      danger: false,
      onConfirm: async () => {
        setBulkProcessing(true);
        try {
          const response = await axios.post(`${API}/admin/payouts/bulk-confirm`, selectedIds);
          toast.success(`${response.data.confirmed} von ${response.data.total} Auszahlungen bestätigt`);
          loadDuePayouts();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Fehler bei der Bulk-Bestätigung');
        } finally {
          setBulkProcessing(false);
        }
      },
    });
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    if (selectedIds.length === duePayouts.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(duePayouts.map(p => p.id));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const selectedTotal = duePayouts
    .filter(p => selectedIds.includes(p.id))
    .reduce((sum, p) => sum + p.amount_eur, 0);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="payout-center">
      <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-heading font-bold">Payout Center</h2>
          <p className="text-muted-foreground text-sm mt-1">
            {duePayouts.length} fällige Auszahlungen
          </p>
        </div>

        {duePayouts.length > 0 && (
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-xs text-muted-foreground">{selectedIds.length} ausgewählt</p>
              <p className="text-lg font-bold" style={{ color: '#C5A059' }}>{formatCurrency(selectedTotal)}</p>
            </div>
            <button
              onClick={bulkConfirm}
              disabled={selectedIds.length === 0 || bulkProcessing}
              className="px-5 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              data-testid="bulk-confirm-button"
            >
              {bulkProcessing ? (
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
              <span>Ausgewählte bestätigen</span>
            </button>
          </div>
        )}
      </div>

      {duePayouts.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-muted-foreground">Keine fälligen Auszahlungen</p>
        </div>
      ) : (
        <>
          {/* Select all bar */}
          <div className="card mb-4 flex items-center gap-3">
            <input
              type="checkbox"
              checked={selectedIds.length === duePayouts.length && duePayouts.length > 0}
              onChange={toggleAll}
              className="rounded border-gray-300 w-4 h-4"
              data-testid="select-all-payouts"
            />
            <span className="text-sm text-muted-foreground">
              {selectedIds.length === duePayouts.length ? 'Alle abwählen' : 'Alle auswählen'}
            </span>
          </div>

          <div className="grid gap-4">
            {duePayouts.map((payout) => (
              <PayoutCard
                key={payout.id}
                payout={payout}
                selected={selectedIds.includes(payout.id)}
                onToggle={() => toggleSelect(payout.id)}
                onConfirm={confirmPayout}
              />
            ))}
          </div>
        </>
      )}
      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
    </div>
  );
};

const PayoutCard = ({ payout, selected, onToggle, onConfirm }) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) { toast.error('Nur PDF, JPG und PNG erlaubt'); return; }
      if (file.size > 10 * 1024 * 1024) { toast.error('Datei zu groß (max. 10MB)'); return; }
      setReceiptFile(file);
    }
  };

  const handleConfirm = async () => {
    setUploading(true);
    await onConfirm(payout.id, reference, note, receiptFile);
    setUploading(false);
    setShowConfirmModal(false);
    setReceiptFile(null);
    setReference('');
    setNote('');
  };

  return (
    <>
      <div className={`card transition ${selected ? 'ring-2 ring-green-400 bg-green-50/50' : ''}`} data-testid={`payout-card-${payout.id}`}>
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggle}
            className="rounded border-gray-300 w-4 h-4 mt-1"
            data-testid={`payout-checkbox-${payout.id}`}
          />
          <div className="flex-1 flex justify-between items-start">
            <div>
              <p className="text-xl font-bold">{formatCurrency(payout.amount_eur)}</p>
              <p className="text-sm text-muted-foreground">
                Fällig: {format(new Date(payout.due_date), 'dd.MM.yyyy', { locale: de })}
              </p>
              {payout.customer_name && (
                <p className="text-sm font-medium mt-1">
                  {payout.customer_name}
                  <span className="text-xs text-muted-foreground ml-2">{payout.customer_email}</span>
                </p>
              )}
              {payout.product_code && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  {payout.product_code} | Einlage: {formatCurrency(payout.principal_eur)}
                </p>
              )}
            </div>
            <button
              onClick={() => setShowConfirmModal(true)}
              className="btn-primary text-sm"
              data-testid={`confirm-payout-${payout.id}`}
            >
              Einzeln bestätigen
            </button>
          </div>
        </div>
      </div>

      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowConfirmModal(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-heading font-bold mb-4">Auszahlung bestätigen</h3>
            
            <div className="mb-4 p-4 bg-background-subtle rounded">
              <p className="text-sm text-muted-foreground">Betrag</p>
              <p className="text-2xl font-bold">{formatCurrency(payout.amount_eur)}</p>
              {payout.customer_name && (
                <p className="text-sm mt-1">{payout.customer_name}</p>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Referenz (z.B. Transaktions-ID)</label>
                <input
                  type="text"
                  className="input-field"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="TRX-12345..."
                  data-testid="payout-reference-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Beleg hochladen (PDF/JPG/PNG)</label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  data-testid="payout-receipt-file-input"
                />
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-primary transition"
                >
                  {receiptFile ? (
                    <div className="flex items-center justify-center space-x-2">
                      <svg className="w-5 h-5 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm font-medium">{receiptFile.name}</span>
                      <button 
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setReceiptFile(null); }}
                        className="text-error hover:text-red-700"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div>
                      <svg className="w-8 h-8 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <p className="text-sm text-muted-foreground">Klicken zum Auswählen</p>
                      <p className="text-xs text-muted-foreground mt-1">PDF, JPG, PNG (max. 10MB)</p>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Notiz (optional)</label>
                <textarea
                  className="input-field"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  data-testid="payout-note-input"
                />
              </div>

              <div className="flex space-x-3">
                <button onClick={() => setShowConfirmModal(false)} className="btn-secondary flex-1" disabled={uploading}>
                  Abbrechen
                </button>
                <button onClick={handleConfirm} className="btn-primary flex-1" disabled={uploading} data-testid="payout-confirm-submit">
                  {uploading ? 'Wird bestätigt...' : 'Bestätigen'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminPayoutCenter;
